/**
 * Currency utilities for multi-currency support
 */

// Map country codes to currencies
export const getCurrencyFromCountry = (countryCode) => {
  if (!countryCode) return 'USD';

  const currencyMap = {
    // Europe - Euro
    'AT': 'EUR', 'BE': 'EUR', 'CY': 'EUR', 'EE': 'EUR', 'FI': 'EUR',
    'FR': 'EUR', 'DE': 'EUR', 'GR': 'EUR', 'IE': 'EUR', 'IT': 'EUR',
    'LV': 'EUR', 'LT': 'EUR', 'LU': 'EUR', 'MT': 'EUR', 'NL': 'EUR',
    'PT': 'EUR', 'SK': 'EUR', 'SI': 'EUR', 'ES': 'EUR',

    // Georgia - GEL
    'GE': 'GEL',

    // US and territories - USD
    'US': 'USD', 'AS': 'USD', 'GU': 'USD', 'MH': 'USD', 'FM': 'USD',
    'MP': 'USD', 'PW': 'USD', 'PR': 'USD', 'VI': 'USD', 'UM': 'USD',

    // Countries that commonly use USD
    'EC': 'USD', 'SV': 'USD', 'PA': 'USD', 'TL': 'USD', 'ZW': 'USD',

    // Default to USD for all others
  };

  return currencyMap[countryCode.toUpperCase()] || 'USD';
};

// Get currency symbol
export const getCurrencySymbol = (currency) => {
  const symbols = {
    'USD': '$',
    'EUR': '€',
    'GEL': '₾',
    'GBP': '£',
  };
  return symbols[currency] || currency;
};

// Format price with currency
export const formatPrice = (amount, currency = 'USD') => {
  const symbol = getCurrencySymbol(currency);
  const formattedAmount = parseFloat(amount).toFixed(2);

  // For Georgian Lari, symbol goes after
  if (currency === 'GEL') {
    return `${formattedAmount} ${symbol}`;
  }

  // For most currencies, symbol goes before
  return `${symbol}${formattedAmount}`;
};

// Available currencies for payment
export const AVAILABLE_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GEL', name: 'Georgian Lari', symbol: '₾' },
];

// Fallback exchange rates to GEL (Georgian Lari) - used if API is unavailable
const FALLBACK_RATES = {
  'USD': 2.65,  // 1 USD = 2.65 GEL (approximate)
  'EUR': 2.90,  // 1 EUR = 2.90 GEL (approximate)
  'GEL': 1.0,   // 1 GEL = 1 GEL
};

// Cached exchange rates
let cachedRates = null;
let cacheTimestamp = null;
const CACHE_DURATION = 3600000; // 1 hour in milliseconds

/**
 * Fetch current exchange rates from backend API
 * @returns {Promise<Object>} - Exchange rates object
 */
export const fetchExchangeRates = async () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // Return cached rates if still fresh
  if (cachedRates && cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_DURATION) {
    console.log('Using cached exchange rates');
    return cachedRates;
  }

  try {
    console.log('Fetching exchange rates from API');
    const response = await fetch(`${API_BASE_URL}/api/exchange-rates`);

    if (response.ok) {
      const data = await response.json();
      cachedRates = data.rates;
      cacheTimestamp = Date.now();
      console.log('Fetched exchange rates:', data.rates, 'Source:', data.source);
      return data.rates;
    } else {
      console.warn('Failed to fetch exchange rates, using fallback');
      return FALLBACK_RATES;
    }
  } catch (error) {
    console.error('Error fetching exchange rates:', error);
    console.log('Using fallback exchange rates');
    return FALLBACK_RATES;
  }
};

/**
 * Convert amount from any supported currency to GEL
 * @param {number} amount - The amount to convert
 * @param {string} fromCurrency - The source currency code (USD, EUR, GEL)
 * @param {Object} rates - Optional exchange rates object (if not provided, uses cached or fetches new)
 * @returns {number} - The amount in GEL
 */
export const convertToGEL = (amount, fromCurrency, rates = null) => {
  if (!fromCurrency || fromCurrency === 'GEL') {
    return parseFloat(amount);
  }

  // Use provided rates, cached rates, or fallback rates
  const exchangeRates = rates || cachedRates || FALLBACK_RATES;

  const rate = exchangeRates[fromCurrency];
  if (!rate) {
    console.warn(`No exchange rate found for ${fromCurrency}, defaulting to USD rate`);
    return parseFloat(amount) * (exchangeRates['USD'] || FALLBACK_RATES['USD']);
  }

  const convertedAmount = parseFloat(amount) * rate;
  // Round to 2 decimal places for GEL
  return Math.round(convertedAmount * 100) / 100;
};
